/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import ImageZoom from 'react-medium-image-zoom';
import {
  DeleteIcon,
  LeftIcon,
  PlusIcon,
  RightIcon,
} from '../../../../../images/icons';

/**
 * V2 credential file upload component.
 * Files are stored locally and passed back via onFilesChange.
 * Actual API upload happens on save (in the parent).
 */
export const StateCredentialFileV2 = ({
  editMode = true,
  files: initialFiles,
  onFilesChange,
  credentialType,
}) => {
  const [files, setFiles] = useState(initialFiles || []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const fileInputRef = useRef(null);

  const thumbnail = files[currentIndex] || null;

  const updateFiles = (newFiles) => {
    setFiles(newFiles);
    onFilesChange?.(newFiles);
  };

  const handleAddFile = async (event) => {
    let file = event.target.files?.[0];
    if (!file) return;

    try {
      file = await imageCompression(file, { maxSizeMB: 1 });
    } catch {
      // compression failed, use original
    }

    const localUrl = URL.createObjectURL(file);
    const newFile = {
      // Mark as local (not yet uploaded)
      _local: true,
      _file: file,
      url: localUrl,
      file_id: `local-${Date.now()}`,
    };

    const newFiles = [...files, newFile];
    updateFiles(newFiles);
    setCurrentIndex(newFiles.length - 1);

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = (fileId) => {
    const newFiles = files.filter(
      (f) => (f.file_id || f.url) !== fileId,
    );
    updateFiles(newFiles);

    if (currentIndex >= newFiles.length) {
      setCurrentIndex(Math.max(0, newFiles.length - 1));
    }
  };

  // No files: show drag-and-drop style upload area
  if (files.length === 0) {
    return (
      <div
        style={{
          border: '1px dashed #cecece',
          borderRadius: 5,
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 50,
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        <h4
          className="card-text"
          style={{
            textAlign: 'center',
            fontSize: 16,
            marginTop: 20,
          }}
        >
          <p style={{ color: '#899197' }}>
            Drag and drop files here or
          </p>
        </h4>

        <div>
          <label
            htmlFor={`v2-file-${credentialType}`}
            className="btn btn-default"
          >
            Browse
          </label>
          <input
            ref={fileInputRef}
            onChange={handleAddFile}
            type="file"
            id={`v2-file-${credentialType}`}
            style={{
              opacity: 0,
              position: 'absolute',
              bottom: 20,
              right: 0,
              padding: 70,
              cursor: 'pointer',
            }}
          />
        </div>
      </div>
    );
  }

  // Has files: show slider with local controls
  return (
    <div
      style={{
        border: '1px dashed #cecece',
        borderRadius: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 336,
            height: 224,
            backgroundColor: '#D9D9D9E5',
            borderRadius: 5,
            overflow: 'hidden',
          }}
        >
          {thumbnail && (
            <ImageZoom
              image={{
                src: thumbnail.url,
                alt: 'credential_image',
                className: 'img',
                style: {
                  objectFit: 'contain',
                  width: '100%',
                  height: 'auto',
                },
              }}
              zoomImage={{
                src: thumbnail.url,
                alt: 'credential_image',
                style: {
                  height: 'auto',
                  maxWidth: 500,
                  width: 'auto',
                },
              }}
            />
          )}

          {/* Left arrow */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const prev =
                currentIndex - 1 >= 0
                  ? currentIndex - 1
                  : files.length - 1;
              setCurrentIndex(prev);
            }}
            style={{
              position: 'absolute',
              top: '40%',
              left: -7,
              cursor: 'pointer',
            }}
          >
            <LeftIcon />
          </span>

          {/* Right arrow */}
          <span
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const next =
                currentIndex + 1 < files.length
                  ? currentIndex + 1
                  : 0;
              setCurrentIndex(next);
            }}
            style={{
              position: 'absolute',
              top: '40%',
              right: -7,
              cursor: 'pointer',
            }}
          >
            <RightIcon />
          </span>

          {editMode && (
            <>
              {/* Add file */}
              <label
                style={{
                  position: 'absolute',
                  bottom: -11,
                  left: '40%',
                  cursor: 'pointer',
                }}
                htmlFor={`v2-file-${credentialType}`}
              >
                <PlusIcon />
              </label>

              <input
                ref={fileInputRef}
                onChange={handleAddFile}
                type="file"
                id={`v2-file-${credentialType}`}
                style={{ display: 'none' }}
              />

              {/* Delete current file */}
              <span
                onClick={() => {
                  if (thumbnail) {
                    handleDeleteFile(
                      thumbnail.file_id || thumbnail.url,
                    );
                  }
                }}
                style={{
                  position: 'absolute',
                  bottom: -5,
                  left: '54%',
                  cursor: 'pointer',
                }}
              >
                <DeleteIcon />
              </span>
            </>
          )}
        </div>

        {/* Thumbnails row */}
        <div
          style={{
            marginTop: 10,
            height: 90,
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            margin: 20,
            width: 270,
          }}
        >
          {files.map((file, index) => (
            <div
              key={file.file_id || file.url}
              onClick={() => setCurrentIndex(index)}
              style={{
                width: 92,
                height: 62,
                backgroundColor: '#D9D9D9E5',
                borderRadius: 5,
                position: 'relative',
                flex: '0 0 auto',
                maxWidth: '100%',
                marginRight: 10,
                backgroundImage: `url(${file.url})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                border: `1px solid ${
                  index === currentIndex ? 'grey' : 'white'
                }`,
              }}
            >
              {editMode && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDeleteFile(
                      file.file_id || file.url,
                    );
                  }}
                  style={{
                    position: 'absolute',
                    bottom: -7,
                    left: '40%',
                    cursor: 'pointer',
                  }}
                >
                  <DeleteIcon />
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
